import React, { useState, useEffect, useCallback, JSX } from 'react';
import { Button } from './ui/button';
import {
  CheckCircle,
  Star,
  CreditCard,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'sonner';
import api from '../utils/api';
import PricingPlans from './PricingPlans';

interface PricingProps {
  onSuccess?: (data: any) => void;
}
type Plan = {
  id: string;
  title: string;
  description: string;
  icon: JSX.Element;
  color: string;
  pricePerSeat: number;
  setupFee: number;
  features: string[];
  highlight?: string;
};

type SelectedPlan = {
  plan: Plan;
  seats: number;
  billingCycle: 'monthly' | 'yearly';
  totalPrice: number;
};

export function Pricing({ onSuccess }: PricingProps) {
  const [selectedPlan, setSelectedPlan] = useState<SelectedPlan | null>(null);
  console.log('selectedPlan:', selectedPlan);

  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    workspaceName: '',
  });

  // Subdomain validation
  const [subdomainSuggestions, setSubdomainSuggestions] = useState<string[]>(
    []
  );
  const [selectedSubdomain, setSelectedSubdomain] = useState('');
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const [subdomainStatus, setSubdomainStatus] = useState<
    'available' | 'taken' | 'checking' | null
  >(null);

  const stripePromise = loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!
  );

  // Utility functions for subdomain handling
  const generateSubdomain = (name: string): string => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 20);
  };

  const generateSubdomainSuggestions = useCallback(
    async (baseName: string): Promise<string[]> => {
      try {
        const response = await api.post('/api/workspace/suggest-subdomain', {
          baseName,
        });

        const suggestions = response.data.suggestions || [];
        console.log(
          `Received ${suggestions.length} subdomain suggestions from API:`,
          suggestions
        );

        return suggestions;
      } catch (error) {
        console.error('Error generating suggestions:', error);
        // Fallback to local generation if API fails
        const base = generateSubdomain(baseName);
        if (!base) return ['myworkspace', 'coaching', 'practice'];

        const suggestions = [
          base,
          `${base}coach`,
          `${base}coaching`,
          `${base}practice`,
          `${base}hub`,
          `my${base}`,
        ];

        console.log('Using fallback suggestions:', suggestions);
        return [...new Set(suggestions)].slice(0, 5);
      }
    },
    []
  );
  const checkSubdomainAvailability = async (
    subdomain: string
  ): Promise<boolean> => {
    try {
      const response = await api.post('/api/workspace/check-subdomain', {
        subdomain,
      });

      return response.data.available;
    } catch (error) {
      console.error('Error checking subdomain:', error);
      return false;
    }
  };

  // Effect to check subdomain availability when it changes
  useEffect(() => {
    const handleSubdomainCheck = async (subdomain: string) => {
      if (!subdomain || selectedPlan?.plan?.id !== 'practitioner') return;

      setIsCheckingSubdomain(true);
      setSubdomainStatus('checking');

      try {
        const isAvailable = await checkSubdomainAvailability(subdomain);
        setSubdomainStatus(isAvailable ? 'available' : 'taken');

        if (!isAvailable && formData.workspaceName) {
          // Generate new suggestions if current one is taken
          const suggestions = await generateSubdomainSuggestions(
            formData.workspaceName
          );
          setSubdomainSuggestions(suggestions);
        }
      } catch {
        setSubdomainStatus(null);
      } finally {
        setIsCheckingSubdomain(false);
      }
    };

    if (selectedSubdomain && selectedPlan?.plan?.id === 'practitioner') {
      const timeoutId = setTimeout(() => {
        handleSubdomainCheck(selectedSubdomain);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
  }, [
    selectedSubdomain,
    selectedPlan,
    formData.workspaceName,
    generateSubdomainSuggestions,
  ]);

  const handleFormChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Auto-generate subdomain for practitioners when workspace name changes
    if (
      field === 'workspaceName' &&
      selectedPlan?.plan?.id === 'practitioner'
    ) {
      const newSubdomain = generateSubdomain(value);
      setSelectedSubdomain(newSubdomain);
      setSubdomainStatus(null);

      if (newSubdomain) {
        // Generate suggestions immediately
        generateSubdomainSuggestions(value).then((suggestions) => {
          setSubdomainSuggestions(suggestions);
        });
      }
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const submitData = {
        id: selectedPlan.plan.id,
        title: selectedPlan.plan.title,
        description: selectedPlan.plan.description,
        workspace: formData.workspaceName,
        email: formData.email,
        seatCount: selectedPlan.seats,
        pricingPeriod:
          selectedPlan.billingCycle === 'monthly' ? 'month' : 'year',
        subdomain:
          selectedPlan.plan.id === 'practitioner' && selectedSubdomain
            ? selectedSubdomain
            : undefined,
        price: selectedPlan.totalPrice,
        setupFee: selectedPlan.plan.setupFee,
      };

      const response = await api.post('/api/workspace/create', submitData, {
        headers: { 'Content-Type': 'application/json' },
      });

      const { sessionId } = response.data;
      const stripe = await stripePromise;

      if (stripe && sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId });
        if (error) {
          toast.error('Failed to redirect to checkout');
        } else {
          setShowModal(false);
          onSuccess?.(response.data);
        }
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error occurred');
      console.error('Error creating workspace:', error.response?.data?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className='w-full max-w-7xl mx-auto px-4 py-12'>
      <div className='text-center mb-12'>
        <div className='inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-full border border-blue-200 mb-6'>
          <Star className='w-4 h-4 text-blue-600 mr-2' />
          <span className='text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'>
            Choose Your Plan
          </span>
        </div>
        <h2 className='text-4xl md:text-5xl font-bold mb-6'>
          Transform Your{' '}
          <span className='bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent'>
            Coaching Experience
          </span>
        </h2>
        <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
          Whether you're an organization seeking comprehensive coaching
          solutions or a practitioner building your own branded platform, we
          have the perfect plan for you.
        </p>
      </div>

      {/* Pricing Cards */}
      <PricingPlans
        setSelectedPlan={setSelectedPlan}
        setShowModal={setShowModal}
      />

      {/* Enhanced Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className='max-w-3xl max-h-[95vh] overflow-y-auto bg-gradient-to-br from-white to-gray-50 border-0 shadow-2xl'>
          <DialogHeader className='space-y-4 pb-6 border-b border-gray-200'>
            <div className='flex items-center space-x-3'>
              <div className=''>
                <DialogTitle className='text-2xl font-bold text-gray-900'>
                  {selectedPlan?.plan?.id === 'organization'
                    ? 'Organization'
                    : 'Practitioner'}{' '}
                  Setup
                </DialogTitle>
                <p className='text-gray-600 mt-1'>
                  {selectedPlan?.plan?.id === 'organization'
                    ? "Set up your organization's coaching platform"
                    : 'Create your custom branded coaching practice'}
                </p>
              </div>
            </div>
          </DialogHeader>

          <div className='py-8 space-y-2'>
            <div className='space-y-2'>
              <Label
                htmlFor='email'
                className='text-sm font-medium text-gray-700'
              >
                Email Address *
              </Label>
              <Input
                id='email'
                type='email'
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                placeholder='your@email.com'
                className='h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500'
              />
            </div>

            {selectedPlan?.plan?.id === 'practitioner' && (
              <div className='space-y-2'>
                <Label
                  htmlFor='workspaceName'
                  className='text-sm font-medium text-gray-700'
                >
                  Workspace Name *
                </Label>
                <Input
                  id='workspaceName'
                  value={formData.workspaceName}
                  onChange={(e) =>
                    handleFormChange('workspaceName', e.target.value)
                  }
                  placeholder='Your workspace name'
                  className='h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                />
                <div className='space-y-3'>
                  <div className='bg-blue-50 border border-blue-200 rounded-lg p-3'>
                    <p className='text-sm text-blue-800 mb-2'>
                      <strong>Choose your subdomain:</strong>
                    </p>
                    <div className='flex items-center space-x-2'>
                      <Input
                        value={selectedSubdomain}
                        onChange={(e) => setSelectedSubdomain(e.target.value)}
                        placeholder='yoursubdomain'
                        className='flex-1 h-10 text-sm'
                      />
                      <span className='text-sm text-gray-600'>
                        .briefscoach.com
                      </span>
                      {isCheckingSubdomain && (
                        <RefreshCw className='w-4 h-4 text-blue-500 animate-spin' />
                      )}
                    </div>

                    {subdomainStatus && (
                      <div
                        className={`mt-2 flex items-center space-x-1 text-sm ${
                          subdomainStatus === 'available'
                            ? 'text-green-600'
                            : subdomainStatus === 'taken'
                            ? 'text-red-600'
                            : 'text-blue-600'
                        }`}
                      >
                        {subdomainStatus === 'available' && (
                          <CheckCircle className='w-4 h-4' />
                        )}
                        {subdomainStatus === 'taken' && (
                          <AlertCircle className='w-4 h-4' />
                        )}
                        <span>
                          {subdomainStatus === 'available' &&
                            'Subdomain is available!'}
                          {subdomainStatus === 'taken' && 'Subdomain is taken'}
                          {subdomainStatus === 'checking' &&
                            'Checking availability...'}
                        </span>
                      </div>
                    )}
                  </div>

                  {subdomainSuggestions.length > 0 && (
                    <div
                      className={`rounded-lg p-3 ${
                        subdomainStatus === 'taken'
                          ? 'bg-yellow-50 border border-yellow-200'
                          : 'bg-blue-50 border border-blue-200'
                      }`}
                    >
                      <div className='flex items-center justify-between mb-2'>
                        <p
                          className={`text-sm ${
                            subdomainStatus === 'taken'
                              ? 'text-yellow-800'
                              : 'text-blue-800'
                          }`}
                        >
                          {subdomainStatus === 'taken'
                            ? 'Try these available options:'
                            : 'Other available suggestions:'}
                        </p>
                        <span className='text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full'>
                          API Generated
                        </span>
                      </div>
                      <div className='flex flex-wrap gap-2'>
                        {subdomainSuggestions.map((suggestion) => (
                          <button
                            key={suggestion}
                            onClick={() => setSelectedSubdomain(suggestion)}
                            className={`px-3 py-1 border rounded text-sm transition-colors ${
                              selectedSubdomain === suggestion
                                ? 'bg-blue-500 text-white border-blue-500'
                                : subdomainStatus === 'taken'
                                ? 'bg-white border-yellow-300 hover:bg-yellow-100'
                                : 'bg-white border-blue-300 hover:bg-blue-100'
                            }`}
                          >
                            {suggestion}.briefscoach.com
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className='flex justify-between pt-6 border-t border-gray-200'>
            <Button
              variant='outline'
              className='px-6'
              onClick={() => {
                setShowModal(false);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isLoading}
              className='bg-blue-600 hover:bg-blue-700 px-8'
            >
              {isLoading ? 'Processing...' : 'Complete Purchase'}
              <CreditCard className='ml-2 w-4 h-4' />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Pricing;
